/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @version 1.23.0
 * @since 1.14.0
 */

import { Fragment } from 'react';

import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import { Select } from '@components/common';
import { DropDownFilterButtonSize } from '@components/common/DropDownFilter/DropDownFilter';
import { FormLabelStyled } from '@components/common/FormStyled/FormStyled';
import { LabelMarkStyled } from '@components/common/Input/styled/InputStyled';
import { IOptionItemProps, ISelectProps } from '@components/common/Select/Select';

import { makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import FormField, { IFormFieldProps } from './FormField';

type TFormFieldSelect = IFormFieldProps &
    Omit<ISelectProps, 'translation' | 'selectedOption' | 'kind' | 'onSelect'> &
    Partial<Pick<ISelectProps, 'onSelect' | 'maxWidth'>>;

const FormFieldSelect = (props: TFormFieldSelect) => {
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const { setFieldTouched, setFieldValue, validateField } = useFormikContext();

    return (
        <FormField {...props}>
            {({ field, kind, label, dataName, required }) => {
                const onSelectHandler = (value: IOptionItemProps) => {
                    props.onSelect?.(value);
                    setFieldValue(field.name, value);
                    setTimeout(() => {
                        validateField(field.name);
                    });
                };

                return (
                    <Fragment>
                        {label && (
                            <FormLabelStyled>
                                {label}
                                {required && <LabelMarkStyled>*</LabelMarkStyled>}
                            </FormLabelStyled>
                        )}
                        <Select
                            dataName={dataName}
                            filterButtonSize={DropDownFilterButtonSize.medium}
                            kind={kind}
                            maxWidth={props.maxWidth}
                            options={props.options || []}
                            placeholder={props.placeholder}
                            selectedOption={field.value}
                            translation={translationCommon}
                            onCloseClick={() => setFieldTouched(field.name)}
                            onSelect={onSelectHandler}
                        />
                    </Fragment>
                );
            }}
        </FormField>
    );
};

export default FormFieldSelect;
